import { For, Index, Show, type JSX } from "solid-js";

import { ArchArt } from "../art/arch_art";
import styles from "../deck.module.css";
import { useDeckWords } from "../deck_words";
import type { VisitsSlide as VisitsSlideData } from "../types";

// Past this many visits the cards wrap to a second row and drop their mouth pictures to stay on the slide.
const VISITS_PER_ROW = 4;
// Past this many lines one visit's work is listed in two columns.
const ITEMS_PER_COLUMN = 8;

/** The plan visit by visit: what happens at each, by plain name, and the date only once a visit is booked. One visit
 *  is no timeline: it is the mouth and what happens, with no "Visit 1" to count from. */
export function VisitsSlide(props: { slide: VisitsSlideData }): JSX.Element {
  const words = useDeckWords();
  const only = () => (props.slide.visits.length === 1 ? props.slide.visits[0] : undefined);
  return (
    <>
      <h2 class={styles["s-h"]}>{props.slide.headline}</h2>
      <Show when={only()} fallback={<VisitCards slide={props.slide} />}>
        {(visit) => (
          <div class={styles["visit-one"]}>
            <div class={styles["vo-arch"]}>
              <ArchArt
                arch={visit().arch}
                mode="mini"
                numbers={false}
                title={words().t("teethInYourVisit")}
              />
            </div>
            <div>
              <Show when={visit().date}>
                {(date) => <p class={styles["vo-d"]}>{words().dateWithWeekday(date())}</p>}
              </Show>
              <ul
                class={styles["vo-items"]}
                data-long={visit().items.length > ITEMS_PER_COLUMN ? "" : undefined}
              >
                <For each={visit().items}>{(item) => <li>{item}</li>}</For>
              </ul>
            </div>
          </div>
        )}
      </Show>
    </>
  );
}

function VisitCards(props: { slide: VisitsSlideData }): JSX.Element {
  const words = useDeckWords();
  return (
    <ol
      class={styles["visits"]}
      style={{ "--n": Math.min(Math.max(props.slide.visits.length, 1), VISITS_PER_ROW) }}
      data-many={props.slide.visits.length > VISITS_PER_ROW ? "" : undefined}
    >
      <Index each={props.slide.visits}>
        {(visit) => (
          <li class={styles["vc"]}>
            <span class={styles["vc-n"]} aria-hidden="true">
              {visit().number}
            </span>
            <div class={styles["vc-box"]}>
              <div class={styles["vc-arch"]}>
                <ArchArt
                  arch={visit().arch}
                  mode="mini"
                  numbers={false}
                  title={words().t("teethInVisit", { number: visit().number })}
                />
              </div>
              <h3 class={styles["vc-t"]}>{words().t("visitNumber", { number: visit().number })}</h3>
              <Show when={visit().date}>
                {(date) => <p class={styles["vc-d"]}>{words().dateWithWeekday(date())}</p>}
              </Show>
              <ul class={styles["vc-chips"]}>
                <For each={visit().items}>{(item) => <li>{item}</li>}</For>
              </ul>
            </div>
          </li>
        )}
      </Index>
    </ol>
  );
}
